import { useEffect, useReducer } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as CustomerActions from '../../../common/services/customer/Actions';
import * as CustomerService from '../../../common/services/customer/Service';
import * as Actions from '../../../common/services/tyre/Actions';
import * as TyreService from '../../../common/services/tyre/Service';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../base/RootReducer';
import { config } from '../../common/';
import { AppThunkDispatch } from '../../common/types';

import classNames from 'classnames';
import Spinner from '../../../common/components/misc/Spinner';
import { CustomerDataDTO } from '../../../common/types/dto/CustomerDataDTO';
import { LookupValueDTO } from '../../../common/types/dto/LookupValueDTO';
import { TyreAmountDTO } from '../../../common/types/dto/TyreAmountDTO';
import { TyreContactInputDTO } from '../../../common/types/dto/TyreContactInputDTO';
import { TyreDTO } from '../../../common/types/dto/TyreDTO';
import DealerDistrictSelect from '../../common/Components/DealerDistrictSelect';
import VehicleSelectV3 from '../../common/Components/VehicleSelectV3';
import { useHistory } from '../../common/RouterHooks';

interface TyreAmount {
    tyreId: number;
    amount: number;
}

interface ContactState {
    dealerCode: number;
    vehcode: number;
    firstName: string;
    lastName: string;
    preferredMethod: string;
    email: string;
    phone: string;
    tyreAmount: TyreAmount[];
    errorText: string;
}
interface ContactProps {
    tyreList: TyreDTO[];
}
interface ReduxStateProps {
    isCommunicatingTyreContact: boolean;
    customerData: CustomerDataDTO | undefined;

    dispatch: AppThunkDispatch;
}

interface ReduxDispatchProps {
    tyreActions: typeof Actions;
    tyreServiceActions: TyreService.ServiceActions;
    customerActions: typeof CustomerActions;
    customerServiceActions: CustomerService.ServiceActions;
}

type ContactProperties =
    ReduxStateProps
    & ContactProps
    & ReduxDispatchProps;

const Contact: React.FC<ContactProperties> = (Props) => {
    const initialState: ContactState = {
        dealerCode: 0,
        vehcode: 0,
        firstName: '',
        lastName: '',
        preferredMethod: '',
        email: '',
        phone: '',
        tyreAmount: [],
        errorText: '',
    };

    const [state, setState]: [ContactState, React.Dispatch<any>] = useReducer(
        (oldState, newState) => ({ ...oldState, ...newState }), initialState,
    );
    const history = useHistory();
    const { t } = useTranslation();

    useEffect(() => {
        if (!Props.tyreList || Props.tyreList.length === 0) {
            history.push(`${config.BaseURL}/tyre/simulator/`);
        } else {
            const tyreAmount = state.tyreAmount;
            Props.tyreList.forEach((x) => {
                tyreAmount.push({
                    tyreId: x.tyreId,
                    amount: 1,
                });
            });
            setState({
                tyreAmount,
                firstName: Props.customerData && Props.customerData.firstName,
                lastName: Props.customerData && Props.customerData.lastName,
                email: Props.customerData && Props.customerData.email,
                phone: Props.customerData && Props.customerData.phoneNumber,
            });
        }
    }, []);

    function onDealerChange(selected: LookupValueDTO<number>) {
        if (selected) {
            setState({
                dealerCode: selected.value,
            });
        } else {
            setState({ dealerCode: 0 });
        }
    }

    function onVehicleChange(vehcode: number) {
        if (state.vehcode !== vehcode) {
            setState({ vehcode });
        }
    }

    function onFirstNameChange(firstName: string) {
        setState({ firstName });
    }

    function onLastNameChange(lastName: string) {
        setState({ lastName });
    }

    function onEmailChange(email: string) {
        setState({ email });
    }

    function onPhoneChange(phone: string) {
        setState({ phone });
    }

    function onPreferredMethodChange(preferredMethod: string) {
        setState({ preferredMethod });
    }

    function GetAmount(tyreId: number): number {
        if (state.tyreAmount && state.tyreAmount.length > 0) {
            const tAElem = state.tyreAmount.filter((y) => y.tyreId === tyreId);
            if (tAElem && tAElem.length > 0) {
                return tAElem[0].amount;
            }
        }
        return 0;
    }

    function OnTyreAmountChange(tyreId: number, selected: LookupValueDTO<string>) {
        const amount = parseInt(selected.value, 10);
        setState({
            tyreAmount: state.tyreAmount.map((el) => (el.tyreId === tyreId ? Object.assign({}, el, { amount }) : el)),
        });
    }

    async function Submit(e: any) {
        e.preventDefault();
        if (!ValidateSubmit()) {
            return;
        }

        const tyreAmount: TyreAmountDTO[] = [];
        state.tyreAmount.forEach((x) => {
            const tAElem = Props.tyreList.filter((y) => y.tyreId === x.tyreId)[0];
            tyreAmount.push({
                amount: x.amount,
                tyre: tAElem,
            });
        });

        const request: TyreContactInputDTO = {
            dealerCode: state.dealerCode,
            email: state.email,
            firstName: state.firstName,
            lastName: state.lastName,
            phone: state.phone,
            preferredMethod: state.preferredMethod,
            vehcode: state.vehcode,
            tyreAmount,
        };

        await Props.tyreServiceActions.tyreContact(request);
        history.push(`${config.BaseURL}/tyre/contact/confirm`);
    }

    function ValidateSubmit(): boolean {
        if (!state.firstName || state.firstName === '') {
            setState({ errorText: `*Nome* deve ser preenchido.`});
            return false;
        }
        if (!state.lastName || state.lastName === '') {
            setState({ errorText: `*Apelido* deve ser preenchido.` });
            return false;
        }
        if (!state.preferredMethod || state.preferredMethod === '') {
            setState({ errorText: `*Método de contacto* deve ser preenchido.` });
            return false;
        }
        if (!state.email || state.email === '') {
            setState({ errorText: `*E-Mail* deve ser preenchido.` });
            return false;
        }
        if (state.preferredMethod === 'phone' && !state.phone || state.phone === '') {
            setState({ errorText: `*Telemóvel* deve ser preenchido (Método de contacto).` });
            return false;
        }
        if (state.dealerCode <= 0) {
            setState({ errorText: `*Ponto de Serviço Autorizado* deve ser preenchido.` });
            return false;
        }
        if (state.vehcode <= 0) {
            setState({ errorText: `*Viatura* deve ser preenchido.` });
            return false;
        }
        const zeroAmountEl = state.tyreAmount.find((x) => x.amount === 0);
        if (zeroAmountEl) {
            setState({ errorText: `A quantidade dos *Pneus* não pode ser 0.` });
            return false;
        }
        setState({ errorText: '' });
        return true;
    }

    return (
        <React.Fragment>
            <div className={classNames(['grid-container', 'fluid', 'margin-top-2'])}>
                <div className={classNames(['grid-x', 'grid-margin-x', 'grid-margin-y'])}>
                    <div className={classNames(['cell', 'small-12', 'medium-6'])}>
                        <h4>Dados de contacto</h4>
                        <label htmlFor="nome" className={classNames(['form-label'])}>Nome *
                            <input id="nome" name="nome" type="text" onChange={(e) => onFirstNameChange(e.currentTarget.value)} value={state.firstName} />
                        </label>
                        <label htmlFor="apelido" className={classNames(['form-label'])}>Apelido *
                            <input id="apelido" name="apelido" type="text" onChange={(e) => onLastNameChange(e.currentTarget.value)} value={state.lastName} />
                        </label>
                        <label className={classNames(['form-label'])}>{t('tyre:contact.method')}
                            <div className={'form-radio-group'}>
                                <div className={'form-radio'}>
                                    <input id="metodoemail" name="contact" type="radio" value="email" onChange={() => onPreferredMethodChange('email')} />
                                    <label htmlFor="metodoemail">E-Mail</label>
                                </div>
                                <div className={'form-radio'}>
                                    <input id="metodophone" name="contact" type="radio" value="phone" onChange={() => onPreferredMethodChange('phone')} />
                                    <label htmlFor="metodophone">{t('tyre:contact.phone')}</label>
                                </div>
                            </div>
                        </label>
                        <label htmlFor="email" className={classNames(['form-label'])}>E-Mail *
                            <input id="email" name="email" type="text" onChange={(e) => onEmailChange(e.currentTarget.value)} value={state.email} />
                        </label>
                        <label htmlFor="phone" className={classNames(['form-label'])}>{t('tyre:contact.phone')}
                            <input id="phone" name="phone" type="text" onChange={(e) => onPhoneChange(e.currentTarget.value)} value={state.phone} />
                        </label>
                    </div>
                    <div className={classNames(['cell', 'small-12', 'medium-6'])}>
                        <h4>{t('tyre:contact.dealer-info')}</h4>
                        <DealerDistrictSelect
                            validation={true}
                            onChange={onDealerChange}
                            staticDealerCode={0}
                            disbaleSelectorOnSubmit={false}
                            sideBySideSelectorsDisplay={false}
                            selectClassName={'foundation-input'}
                        />
                        <h4 className={'margin-top-2'}>Lista de pneus</h4>
                        {Props.tyreList.map((x, id) => {
                            return (
                                <div key={'tyreitem' + id} className={'contact-list'}>
                                    <div className={'contact-list-description'}>
                                        <p className={'text-bold'}>{x.description}</p>
                                        <p>{x.tyreBrandName}</p>
                                    </div>
                                    <div className={'contact-list-selector'}>
                                        <select
                                            className={classNames(['form-control', 'base-select', 'foundation-input'])}
                                            value={GetAmount(x.tyreId)}
                                            onChange={(y) => OnTyreAmountChange(x.tyreId, y.target)}
                                        >
                                            <option value={0}>0</option>
                                            <option value={1}>1</option>
                                            <option value={2}>2</option>
                                            <option value={3}>3</option>
                                            <option value={4}>4</option>
                                        </select>
                                    </div>
                                    <div className={'separator'} />
                                </div>);
                        })}
                    </div>
                    <div className={classNames(['cell', 'small-12', 'medium-12'])}>
                        <h4>Selecione a viatura</h4>
                        <VehicleSelectV3 onChange={onVehicleChange} isMini={false} />
                    </div>
                    <div className={classNames(['cell', 'small-12', 'medium-12', 'margin-top-3'])}>
                        <div className={'tyres-footer'}>
                            <div className="error-message">{state.errorText}</div>
                            <button className={classNames(['button', 'bttSubscrever'])} onClick={(e) => Submit(e)}>Solicitar Proposta</button>
                            <p>{t('tyre:contact.footer1')}</p>
                            <p className={'margin-top-1'}>
                                {t('tyre:contact.footer2')}
                                <a href="https://www.bmw.pt/pt/footer/metanavigation/data-privacy.html" target="_blank" style={{ textDecoration: 'underline' }}>
                                    <strong>política de privacidade</strong>
                                </a>
                                {t('tyre:contact.footer3')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            {(Props.isCommunicatingTyreContact) && <Spinner />}
        </React.Fragment>
    );
};

const mapStateToProps = (store: RootState) => ({
    isCommunicatingTyreContact: store.tyreState.tyreContact.isCommunicating,
    customerData: store.customerState.customerData.data.customerData,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    tyreActions: bindActionCreators(Actions, dispatch),
    tyreServiceActions: TyreService.bindServiceActionCreators(config, dispatch),
    customerActions: bindActionCreators(CustomerActions, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(Contact);
